// ④ワールド設計の幾何ヘルパー — 設計 fills・段状標高から地表と歩行可否を決定的に計算する。
// ⑤の機械反映（建物・NPC・敵の Y、木立の展開、建物群の水域・道路回避）と
// ④の幾何品質チェックの両方から使う純関数群。エンジンの blockFill 適用
// （配列順で後勝ち・rect/circle/ellipse・replaceOnly/requiresBelow・scatter）と
// overworld の帯状標高（Z 3 分割）を模倣する。数値の閾値はすべて呼び出し側の
// config（story-pipeline.json）から渡す。

const asArray = (value) => (Array.isArray(value) ? value : []);
const finite = (value, fallback) => (Number.isFinite(Number(value)) ? Number(value) : fallback);

/** 文字列から 32bit シードを作る（決定的・プロセス非依存） */
export function hashSeed(text) {
    let h = 0x811c9dc5;
    const s = String(text ?? '');
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}

/** エンジンの hash2D と同系の決定的 2D ハッシュ */
export function hash2D(seed, x, z) {
    let h = seed >>> 0;
    h = Math.imul(h ^ Math.imul(x >>> 0, 0x85ebca6b), 0xc2b2ae35);
    h = Math.imul(h ^ Math.imul(z >>> 0, 0x27d4eb2d), 0x165667b1);
    h ^= h >>> 15;
    h = Math.imul(h, 0x85ebca6b);
    h ^= h >>> 13;
    return h >>> 0;
}

export function hashToUnit(hash) {
    return (hash >>> 0) / 4294967296;
}

/** fill の XZ 足面が (x, z) を含むか（エンジンの円・楕円判定と同じ「中心+半径」規約） */
export function fillCoversPoint(fill, x, z) {
    const pos = asArray(fill.pos);
    if (fill.shape === 'circle') {
        const dx = x - finite(pos[0], 0);
        const dz = z - finite(pos[2], 0);
        const r = finite(fill.radius, 1);
        return dx * dx + dz * dz <= r * r;
    }
    if (fill.shape === 'ellipse') {
        const dx = x - finite(pos[0], 0);
        const dz = z - finite(pos[2], 0);
        const rx = Math.max(1, finite(fill.radiusX, 1));
        const rz = Math.max(1, finite(fill.radiusZ, 1));
        return (dx * dx) / (rx * rx) + (dz * dz) / (rz * rz) <= 1;
    }
    const size = asArray(fill.size);
    return x >= finite(pos[0], 0) && x < finite(pos[0], 0) + finite(size[0], 1)
        && z >= finite(pos[2], 0) && z < finite(pos[2], 0) + finite(size[2], 1);
}

/**
 * (x, z) の垂直カラムへ fills を配列順に適用し、最上部の非 AIR ブロックを返す。
 * scatterProbability<1（確率配置）と heightMode=terrainSurface（overworld 専用）は
 * 確定した地面にならないため無視する。
 * @returns {{ y: number, blockType: string } | null}
 */
export function surfaceInfoFromFills(fills, x, z, worldHeight = 100) {
    const column = new Array(worldHeight).fill('AIR');
    for (const fill of asArray(fills)) {
        if (Number.isFinite(Number(fill.scatterProbability)) && Number(fill.scatterProbability) < 1) continue;
        if (fill.heightMode === 'terrainSurface' || fill.heightMode === 'terrain_surface') continue;
        if (!fillCoversPoint(fill, x, z)) continue;
        const pos = asArray(fill.pos);
        const y0 = Math.max(0, Math.floor(finite(pos[1], 0)));
        const sizeY = fill.shape === 'circle' || fill.shape === 'ellipse'
            ? Math.max(1, Math.floor(finite(fill.height, 1)))
            : Math.max(1, Math.floor(finite(asArray(fill.size)[1], 1)));
        const blockType = asArray(fill.blockTypes)[0] ?? fill.blockType ?? 'AIR';
        for (let y = y0; y < Math.min(worldHeight, y0 + sizeY); y++) {
            if (fill.requiresBelow) {
                const below = y > 0 ? column[y - 1] : 'AIR';
                if (!fill.requiresBelow.includes(below)) continue;
            }
            if (fill.replaceOnly && !fill.replaceOnly.includes(column[y])) continue;
            column[y] = blockType;
        }
    }
    for (let y = worldHeight - 1; y >= 0; y--) {
        if (column[y] !== 'AIR') return { y, blockType: column[y] };
    }
    return null;
}

/** 設計 fills の最上端 Y（花・小物 fill の走査上限などに使う） */
export function maxFillTopY(fills, worldHeight = 100) {
    let top = 0;
    for (const fill of asArray(fills)) {
        const pos = asArray(fill.pos);
        const y0 = Math.max(0, Math.floor(finite(pos[1], 0)));
        const sizeY = fill.shape === 'circle' || fill.shape === 'ellipse'
            ? Math.max(1, Math.floor(finite(fill.height, 1)))
            : Math.max(1, Math.floor(finite(asArray(fill.size)[1], 1)));
        top = Math.max(top, Math.min(worldHeight - 1, y0 + sizeY - 1));
    }
    return top;
}

/** 点 (px, pz) から線分への最短距離の 2 乗（エンジン同等） */
export function segmentDistSq(px, pz, ax, az, bx, bz) {
    const vx = bx - ax;
    const vz = bz - az;
    const wx = px - ax;
    const wz = pz - az;
    const c1 = wx * vx + wz * vz;
    const c2 = vx * vx + vz * vz;
    if (c1 <= 0) return wx * wx + wz * wz;
    if (c2 <= c1) return (px - bx) ** 2 + (pz - bz) ** 2;
    const t = c1 / c2;
    const qx = ax + t * vx;
    const qz = az + t * vz;
    return (px - qx) ** 2 + (pz - qz) ** 2;
}

/** overworld の帯状標高（ノイズ除く）。エンジン getTerrainHeight の帯分岐を再現する */
export function overworldBandHeight(terrain, z) {
    const b = terrain.boundaries ?? {};
    const e = terrain.elevation ?? {};
    const lowerDivide = finite(b.lowerUpperDivide, 504);
    const castleDivide = finite(b.castleUpperDivide, 120);
    if (z >= lowerDivide) return Math.floor(finite(e.lowerCityHeight, 9));
    if (z > castleDivide) return Math.floor(finite(e.upperCityHeight, 9));
    return Math.floor(finite(e.castleBaseHeight, 9));
}

/** 道路（軸平行ストリップ・range 対応）が (x, z) を含むか */
export function roadCoversPoint(road, x, z) {
    const width = Math.max(1, finite(road.width, 5));
    const along = road.axis === 'z' ? x : z;
    const across = road.axis === 'z' ? z : x;
    if (Math.abs(across - finite(road.position, 0)) >= width / 2) return false;
    const range = asArray(road.range);
    if (range.length === 2) return along >= range[0] && along < range[1];
    return true;
}

/**
 * 設計から (x, z) の地表情報 { y, blockType } を返す。
 * void: fills のカラム合成。overworld: 帯状標高＋（道路 > 用水路 > 環状水路 > 川 > エリア表面）。
 */
export function designGroundInfo(design, x, z) {
    const worldHeight = design.scale?.worldSize?.height ?? 100;
    if (design.foundation?.generator !== 'overworld') {
        return surfaceInfoFromFills([...asArray(design.foundation?.fills), ...asArray(design.detailFills)], x, z, worldHeight);
    }
    const terrain = design.terrain ?? {};
    const y = overworldBandHeight(terrain, z);
    for (const road of asArray(terrain.roads)) {
        if (roadCoversPoint(road, x, z)) return { y, blockType: road.blockType ?? 'COBBLESTONE' };
    }
    for (const canal of asArray(terrain.water?.canals)) {
        const half = finite(canal.waterHalfWidth, 0);
        if (!(half > 0)) continue;
        const path = asArray(canal.path);
        for (let i = 0; i < path.length - 1; i++) {
            if (segmentDistSq(x, z, path[i][0], path[i][1], path[i + 1][0], path[i + 1][1]) <= half * half) {
                return { y, blockType: 'WATER' };
            }
        }
    }
    for (const ring of asArray(terrain.water?.ringCanals)) {
        const radius = finite(ring.radius, 0);
        const half = finite(ring.halfWidth, 0);
        if (!(radius > 0) || !(half > 0)) continue;
        const cx = design.scale?.worldSize?.width ? design.scale.worldSize.width / 2 : 504;
        const cz = design.scale?.worldSize?.depth ? design.scale.worldSize.depth / 2 : 504;
        const dist = Math.hypot(x - cx, z - cz);
        if (dist >= radius - half && dist <= radius + half) return { y, blockType: 'WATER' };
    }
    for (const river of asArray(terrain.water?.rivers)) {
        const xr = asArray(river.xRange);
        const zr = asArray(river.zRange);
        if (x >= finite(xr[0], 1) && x <= finite(xr[1], 0) && z >= finite(zr[0], 1) && z <= finite(zr[1], 0)) {
            return { y, blockType: 'WATER' };
        }
    }
    const areas = [...asArray(design.areas)].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
    for (const area of areas) {
        const b = area.bounds ?? {};
        if (x >= b.minX && x < b.maxX && z >= b.minZ && z < b.maxZ) return { y, blockType: area.surfaceBlock ?? 'GRASS' };
    }
    return { y, blockType: terrain.surface ?? 'GRASS' };
}

/** 地表が歩行（居住）可能か。unwalkable は config の unwalkableSurfaceBlocks */
export function isWalkableSurface(info, unwalkableBlocks) {
    return !!info && !asArray(unwalkableBlocks).includes(info.blockType);
}

/**
 * 形状内へ決定的に count 点を散布する。
 * @param {object} opts { seedText, count, shape, minSpacing, maxAttempts, accept }
 *   shape: { kind: 'rect', minX, maxX, minZ, maxZ } | { kind: 'circle', x, z, radius }
 */
export function scatterPointsInShape({ seedText, count, shape, minSpacing = 0, maxAttempts = null, accept = null }) {
    const seed = hashSeed(seedText);
    const points = [];
    const bbox = shape.kind === 'circle'
        ? { minX: shape.x - shape.radius, maxX: shape.x + shape.radius + 1, minZ: shape.z - shape.radius, maxZ: shape.z + shape.radius + 1 }
        : shape;
    const spanX = Math.max(1, Math.floor(bbox.maxX - bbox.minX));
    const spanZ = Math.max(1, Math.floor(bbox.maxZ - bbox.minZ));
    const attempts = maxAttempts ?? Math.max(count * 8, 16);
    for (let i = 0; i < attempts && points.length < count; i++) {
        const x = Math.floor(bbox.minX) + hash2D(seed, i, 0x9e3779b9) % spanX;
        const z = Math.floor(bbox.minZ) + hash2D(seed, i, 0x85ebca6b) % spanZ;
        if (shape.kind === 'circle') {
            const dx = x - shape.x;
            const dz = z - shape.z;
            if (dx * dx + dz * dz > shape.radius * shape.radius) continue;
        }
        if (minSpacing > 0 && points.some((p) => (p.x - x) ** 2 + (p.z - z) ** 2 < minSpacing * minSpacing)) continue;
        if (accept && !accept(x, z)) continue;
        points.push({ x, z });
    }
    return points;
}

/** 矩形同士の交差（half-open 規約 { minX, maxX, minZ, maxZ }） */
export function rectsIntersect(a, b) {
    return a.minX < b.maxX && a.maxX > b.minX && a.minZ < b.maxZ && a.maxZ > b.minZ;
}

/** fill 足面と矩形の交差（circle/ellipse は最近接点近似） */
export function fillFootprintIntersectsRect(fill, rect) {
    const pos = asArray(fill.pos);
    if (fill.shape === 'circle' || fill.shape === 'ellipse') {
        const cx = finite(pos[0], 0);
        const cz = finite(pos[2], 0);
        const rx = fill.shape === 'circle' ? Math.max(1, finite(fill.radius, 1)) : Math.max(1, finite(fill.radiusX, 1));
        const rz = fill.shape === 'circle' ? Math.max(1, finite(fill.radius, 1)) : Math.max(1, finite(fill.radiusZ, 1));
        const nx = Math.max(rect.minX, Math.min(rect.maxX - 1, cx));
        const nz = Math.max(rect.minZ, Math.min(rect.maxZ - 1, cz));
        const dx = (nx - cx) / rx;
        const dz = (nz - cz) / rz;
        return dx * dx + dz * dz <= 1;
    }
    const size = asArray(fill.size);
    return rectsIntersect({
        minX: finite(pos[0], 0),
        maxX: finite(pos[0], 0) + finite(size[0], 1),
        minZ: finite(pos[2], 0),
        maxZ: finite(pos[2], 0) + finite(size[2], 1),
    }, rect);
}

/** 道路ストリップ（range 対応・margin 拡張）と矩形の交差 */
export function roadIntersectsRect(road, rect, margin = 0) {
    const width = Math.max(1, finite(road.width, 5)) / 2 + margin;
    const position = finite(road.position, 0);
    const range = asArray(road.range);
    const strip = road.axis === 'z'
        ? { minX: range.length === 2 ? range[0] - margin : -Infinity, maxX: range.length === 2 ? range[1] + margin : Infinity, minZ: position - width, maxZ: position + width }
        : { minX: position - width, maxX: position + width, minZ: range.length === 2 ? range[0] - margin : -Infinity, maxZ: range.length === 2 ? range[1] + margin : Infinity };
    return rectsIntersect(strip, rect);
}

/** 折れ線水路（bankHalfWidth 帯・margin 拡張）と矩形の交差（矩形頂点＋辺中点＋中心の 9 点近似） */
export function canalIntersectsRect(canal, rect, margin = 0) {
    const half = Math.max(finite(canal.bankHalfWidth, 0), finite(canal.waterHalfWidth, 0)) + margin;
    if (!(half > 0)) return false;
    const path = asArray(canal.path);
    const xs = [rect.minX, Math.floor((rect.minX + rect.maxX) / 2), rect.maxX - 1];
    const zs = [rect.minZ, Math.floor((rect.minZ + rect.maxZ) / 2), rect.maxZ - 1];
    for (let i = 0; i < path.length - 1; i++) {
        for (const x of xs) {
            for (const z of zs) {
                if (segmentDistSq(x, z, path[i][0], path[i][1], path[i + 1][0], path[i + 1][1]) <= half * half) return true;
            }
        }
    }
    return false;
}

/** 環状水路（中心・半径帯）と矩形の交差（中心からの最近・最遠距離で判定） */
export function ringIntersectsRect(center, radius, halfWidth, rect, margin = 0) {
    const inner = Math.max(0, radius - halfWidth - margin);
    const outer = radius + halfWidth + margin;
    const nx = Math.max(rect.minX, Math.min(rect.maxX - 1, center.x));
    const nz = Math.max(rect.minZ, Math.min(rect.maxZ - 1, center.z));
    const nearest = Math.hypot(nx - center.x, nz - center.z);
    const fx = Math.abs(center.x - rect.minX) > Math.abs(center.x - (rect.maxX - 1)) ? rect.minX : rect.maxX - 1;
    const fz = Math.abs(center.z - rect.minZ) > Math.abs(center.z - (rect.maxZ - 1)) ? rect.minZ : rect.maxZ - 1;
    const farthest = Math.hypot(fx - center.x, fz - center.z);
    return !(farthest < inner || nearest > outer);
}
